import React, { useEffect, useState, useRef, useCallback } from "react";
import { MappingNodeData, HandleConfig } from "../../types";
import { Position } from "reactflow";
import ConfigBoxToolbar from "../../components/shared/ConfigBoxToolbar";
import LabelWithVisibilityToggle, { VisibilityOption } from "../../components/shared/LabelWithVisibilityToggle";
import AutoComplete from "../../components/shared/AutoComplete";
import HandleManager from "../../components/shared/HandleManager";

interface TransformationsConfigBoxProps {
  nodeId: string;
  data: MappingNodeData;
  onUpdate?: (data: Partial<MappingNodeData>) => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  onDetachFromGroup?: () => void; // Add prop
  isAdvancedMode?: boolean;
  portalRoot?: HTMLElement | null;
}

// Extended MappingNodeData to include transformationTypeVisibility and category
interface ExtendedMappingNodeData extends MappingNodeData {
  transformationTypeVisibility?: "hidden" | "visible";
  category?: string;
  categoryVisibility?: "hidden" | "visible";
}

// Simplified interface for transformation suggestions
interface TransformationSuggestion {
  value: string;
  label: string;
  description?: string;
  category: string;
}

const TRANSFORMATION_SUGGESTIONS = [
  // Mapping transformations
  { value: "Direct Assign", label: "Direct Assign", description: "Assign input value directly to the output without modification", category: "Mapping" },
  { value: "Join Values", label: "Join Values", description: "Concatenate multiple values into a single string", category: "Mapping" },
  { value: "Lowercase String", label: "Lowercase String", description: "Convert text to lowercase", category: "Mapping" },
  { value: "Pluck Values", label: "Pluck Values", description: "Extract specific values from arrays or objects", category: "Mapping" },
  { value: "Replace String", label: "Replace String", description: "Replace occurrences of a substring within a string", category: "Mapping" },
  { value: "Split String", label: "Split String", description: "Divide a string into an array based on a delimiter", category: "Mapping" },
  { value: "Substring", label: "Substring", description: "Extract a portion of a string based on start and end positions", category: "Mapping" },
  { value: "To Boolean", label: "To Boolean", description: "Convert a value to a boolean", category: "Mapping" },
  { value: "To Float", label: "To Float", description: "Convert a value to a floating-point number", category: "Mapping" },
  { value: "To Hash", label: "To Hash", description: "Apply a hash function (e.g., MD5, SHA256) to a value", category: "Mapping" },
  { value: "To Rounded Integer", label: "To Rounded Integer", description: "Round a number to the nearest integer", category: "Mapping" },
  { value: "To String", label: "To String", description: "Convert a value to a string", category: "Mapping" },
  { value: "To Truncated Integer", label: "To Truncated Integer", description: "Truncate a number to remove decimal places", category: "Mapping" },
  { value: "Trim String", label: "Trim String", description: "Remove leading and trailing whitespace from a string", category: "Mapping" },
  { value: "Truncate Float", label: "Truncate Float", description: "Limit the number of decimal places in a floating-point number", category: "Mapping" },
  { value: "Uppercase String", label: "Uppercase String", description: "Convert text to uppercase", category: "Mapping" },

  // Enrichment transformations
  {
    value: "Date",
    label: "Date",
    description: "Generates a date or datetime string using the current server's time.",
    category: "Enrichment",
    details: "Requires Go's date formatting syntax to specify the desired date format."
  },
  {
    value: "Direct Value",
    label: "Direct Value",
    description: "Sets a specific value for the enriched output key.",
    category: "Enrichment",
    details: "Supports data types such as String, Boolean, Integer, or Float."
  },
  {
    value: "Pseudo-Random Values",
    label: "Pseudo-Random Values",
    description: "Generates random values as integers or floats within specified min and max parameters.",
    category: "Enrichment",
    details: "Useful for creating randomized data for testing or unique identifiers."
  },
  {
    value: "Timestamp",
    label: "Timestamp",
    description: "Generates a Unix timestamp using the current server's time.",
    category: "Enrichment",
    details: "Output can be formatted to include milliseconds or seconds."
  },
  {
    value: "UUID",
    label: "UUID",
    description: "Assigns a unique identifier as the output key.",
    category: "Enrichment",
    details: "Supports UUID version 1 or version 4; version 4 is recommended to ensure uniqueness."
  },

  // Custom Expression transformations
  { value: "LUA Expression", label: "LUA Expression", description: "Apply custom LUA logic for data transformation", category: "Custom Expression" },
    
  // Spreading
  {
    value: "Flatten Object",
    label: "Flatten Object",
    description: "Traverse and flatten nested objects or arrays into a single-level object.",
    category: "Spreading",
    details: "Requires specifying input key, prefix, separator, depth, and optional array start index."
  }
];

// Group suggestions by category
const groupedSuggestions = TRANSFORMATION_SUGGESTIONS.reduce((acc, suggestion) => {
  if (!acc[suggestion.category]) {
    acc[suggestion.category] = [];
  }
  acc[suggestion.category].push(suggestion);
  return acc;
}, {} as Record<string, TransformationSuggestion[]>);

export default function TransformationsConfigBox({
  data,
  nodeId,
  onUpdate,
  onDelete,
  onDuplicate,
  onDetachFromGroup, // Destructure prop
  portalRoot,
  isAdvancedMode = false,
}: TransformationsConfigBoxProps) {
  // Cast data to ExtendedMappingNodeData to access transformationTypeVisibility
  const extendedData = data as ExtendedMappingNodeData;
  const [name, setName] = useState(data.name || "");
  const [transformationType, setTransformationType] = useState(data.transformationType || "");
  const [notes, setNotes] = useState(data.notes || "");
  const [selectedCategory, setSelectedCategory] = useState<string>("Mapping");

  const [nameVisibility, setNameVisibility] = useState<VisibilityOption>(
    data.nameVisibility === "hidden" ? "hidden" : "visible"
  );
  const [categoryVisibility, setCategoryVisibility] = useState<VisibilityOption>(
    extendedData.categoryVisibility === "hidden" ? "hidden" : "visible"
  );
  const [transformationTypeVisibility, setTransformationTypeVisibility] = useState<VisibilityOption>(
    extendedData.transformationTypeVisibility === "hidden" ? "hidden" : "visible"
  );
  const [notesVisibility, setNotesVisibility] = useState<VisibilityOption>(
    data.notesVisibility === "hidden" ? "hidden" : "visible"
  );

  const defaultHandles: HandleConfig[] = [
    { id: 'default-target', type: 'target', position: Position.Left, color: '#22c55e' },
    { id: 'default-source', type: 'source', position: Position.Right, color: '#3b82f6' },
  ];

  const [handles, setHandles] = useState<HandleConfig[]>(
    Array.isArray(data.handles) ? [...data.handles] : defaultHandles
  );

  const isFirstRender = useRef(true);
  const prevUpdateRef = useRef(""); // Last update sent to parent
  const prevPropsRef = useRef("");  // Last props received from parent

  useEffect(() => {
    const incomingProps = JSON.stringify({
      name: data.name || "",
      transformationType: data.transformationType || "",
      notes: data.notes || "",
      category: (data as any).category || "",
      nameVisibility: data.nameVisibility === "hidden" ? "hidden" : "visible",
      categoryVisibility: (data as any).categoryVisibility === "hidden" ? "hidden" : "visible",
      transformationTypeVisibility: (data as any).transformationTypeVisibility === "hidden" ? "hidden" : "visible",
      notesVisibility: data.notesVisibility === "hidden" ? "hidden" : "visible",
      handles: Array.isArray((data as any).handles) ? (data as any).handles : defaultHandles,
    });

    if (incomingProps !== prevPropsRef.current) {
      prevPropsRef.current = incomingProps;

      setName(data.name || "");
      setTransformationType(data.transformationType || "");
      setNotes(data.notes || "");
      setNameVisibility(data.nameVisibility === "hidden" ? "hidden" : "visible");
      setCategoryVisibility((data as any).categoryVisibility === "hidden" ? "hidden" : "visible");
      setTransformationTypeVisibility((data as any).transformationTypeVisibility === "hidden" ? "hidden" : "visible");
      setNotesVisibility(data.notesVisibility === "hidden" ? "hidden" : "visible");

      if (Array.isArray((data as any).handles)) {
        setHandles([...(data as any).handles]);
      } else {
        setHandles(defaultHandles);
      }

      // Set initial category based on transformationType if it exists
      if (data.transformationType) {
        const foundSuggestion = TRANSFORMATION_SUGGESTIONS.find(
          suggestion => suggestion.value === data.transformationType || suggestion.label === data.transformationType
        );
        if (foundSuggestion) {
          setSelectedCategory(foundSuggestion.category);
        }
      }
    }

    if (isFirstRender.current) {
      isFirstRender.current = false;
    }
  }, [data]);

  useEffect(() => {
    if (isFirstRender.current) return;

    const outgoingUpdate = JSON.stringify({
      name,
      label: transformationType,
      transformationType,
      category: selectedCategory,
      notes,
      nameVisibility,
      categoryVisibility,
      transformationTypeVisibility,
      notesVisibility,
      handles,
    });

    if (outgoingUpdate !== prevUpdateRef.current && onUpdate) {
      prevUpdateRef.current = outgoingUpdate;
      onUpdate({
        name,
        label: transformationType,
        transformationType,
        category: selectedCategory,
        notes,
        nameVisibility,
        categoryVisibility,
        transformationTypeVisibility,
        notesVisibility,
        handles,
      });
    }
  }, [
    name,
    transformationType,
    selectedCategory,
    notes,
    nameVisibility,
    categoryVisibility,
    transformationTypeVisibility,
    notesVisibility,
    handles,
    onUpdate,
  ]);

  const handleHandlesChange = useCallback((newHandles: HandleConfig[]) => {
    setHandles(newHandles);
  }, []);

  const toggleVisibility = (
    current: VisibilityOption,
    setter: React.Dispatch<React.SetStateAction<VisibilityOption>>
  ) => {
    const nextVisibility = current === "hidden" ? "visible" : "hidden";
    setter(nextVisibility);
  };

  const filteredSuggestions = selectedCategory
    ? groupedSuggestions[selectedCategory] || []
    : TRANSFORMATION_SUGGESTIONS;

  return (
    <div className="space-y-3 relative">
      {/* Add the toolbar */}
      <ConfigBoxToolbar
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        onDetachFromGroup={onDetachFromGroup} // Pass prop down
        isAdvancedMode={isAdvancedMode}
      />

      <div className="pt-1">
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 mt-3">
          Transformation Name
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Enter transformation name"
          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:focus:border-blue--600 focus:ring rounded focus:outline-none focus:border-blue--500 focus:ring-blue--500/30 dark:focus:ring-blue--500/20 dark:bg-gray-700 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition duration-300 ease-in-out"
        />
      </div>

      {/* Transformation Category with Visibility */}
      <div>
        <LabelWithVisibilityToggle
          label="Transformation Category"
          visibility={categoryVisibility}
          onToggleVisibility={() => toggleVisibility(categoryVisibility, setCategoryVisibility)}
          className="mb-1"
        />
        <select
          value={selectedCategory}
          onChange={(e) => {
            const newCategory = e.target.value;
            setSelectedCategory(newCategory);
            // Reset transformation type when category changes
            setTransformationType("");
          }}
          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:focus:border-blue--600 focus:ring rounded focus:outline-none focus:border-blue--500 focus:ring-blue--500/30 dark:focus:ring-blue--500/20 dark:bg-gray-700 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition duration-300 ease-in-out"
        >
          {Object.keys(groupedSuggestions).map((category) => (
            <option key={category} value={category}>
              {category}
            </option>
          ))}
        </select>
      </div>

      <div>
        <LabelWithVisibilityToggle
          label="Transformation Type"
          visibility={transformationTypeVisibility}
          onToggleVisibility={() => toggleVisibility(transformationTypeVisibility, setTransformationTypeVisibility)}
          className="mb-1"
        />
        <AutoComplete
          value={transformationType}
          onChange={(value: string) => {
            setTransformationType(value);
          }}
          onSelect={(option) => {
            if (typeof option === 'object') {
              const transformationOption = option as unknown as TransformationSuggestion;
              if (transformationOption.label) {
                setTransformationType(transformationOption.label);
              }
            }
          }}
          options={filteredSuggestions}
          placeholder="Select transformation type"
          dropdownClassName="absolute z-[100]"
          portalContainer={portalRoot}
        />
      </div>

      <div>
        <LabelWithVisibilityToggle
          label="Notes"
          visibility={notesVisibility}
          onToggleVisibility={() => toggleVisibility(notesVisibility, setNotesVisibility)}
          className="mb-1"
        />
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add notes about this transformation (optional)"
          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:focus:border-blue--600 focus:ring rounded focus:outline-none focus:border-blue--500 focus:ring-blue--500/30 dark:focus:ring-blue--500/20 dark:bg-gray-700 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition duration-300 ease-in-out resize-y min-h-[80px] max-h-[400px]"
          maxLength={500} // Limit to 500 characters
        />
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">
          {notes.length}/500
        </div>
      </div>
      {/* --- Handle Manager --- */}
      <div>
        <HandleManager
          nodeId={nodeId}
          handles={handles}
          onChange={handleHandlesChange}
          maxHandles={6}
          portalRoot={portalRoot}
        />
      </div>
    </div>
  );
}
